import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Spinner
} from 'reactstrap';
import classnames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import api, { FILE_BASE_URL } from '../../utils/api';

const ViewJournalist = (props) => {
  const { id } = useParams();
  const [journalist, setJournalist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('1');
  const [images, setImages] = useState([]);
  const [children, setChildren] = useState([]);
  const [statements, setStatements] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [linkedInstitutionData, setLinkedInstitutionData] = useState(null);
  const [linkedActivistsData, setLinkedActivistsData] = useState([]);
  const [linkedJournalistPeersData, setLinkedJournalistPeersData] = useState([]); // 🔗 صحفيون آخرون مرتبطون
  const [statementModalOpen, setStatementModalOpen] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState(null);

  // دالة لإنشاء الدائرة الملونة للصور المفقودة
  const getAvatarCircle = (name, size = '50px', fontSize = '16px') => {
    const firstLetter = name ? name.charAt(0).toUpperCase() : 'ص';
    
    return (
      <div 
        className="rounded-circle d-inline-flex align-items-center justify-content-center"
        style={{
          backgroundColor: '#34c38f', // أخضر للصحفيين
          color: '#ffffff',
          fontSize: fontSize,
          fontWeight: 'bold',
          width: size,
          height: size,
          minWidth: size,
          minHeight: size
        }}
      >
        {firstLetter}
      </div>
    );
  };

  // دالة لإنشاء الدائرة الملونة للمؤسسات
  const getInstitutionAvatarCircle = (name, size = '50px', fontSize = '16px') => {
    const firstLetter = name ? name.charAt(0).toUpperCase() : 'م';
    
    return (
      <div 
        className="rounded d-inline-flex align-items-center justify-content-center"
        style={{
          backgroundColor: '#556ee6', // أزرق للمؤسسات
          color: '#ffffff',
          fontSize: fontSize,
          fontWeight: 'bold',
          width: size,
          height: size,
          minWidth: size,
          minHeight: size
        }}
      >
        {firstLetter}
      </div>
    );
  };

  useEffect(() => {
    props.setBreadcrumbItems('عرض الصحفي', [
      { title: "الرئيسية", link: "/dashboard" },
      { title: "الصحفيين", link: "/dashboard/journalists" },
      { title: "عرض الصحفي", link: "#" }
    ]);
    fetchJournalist();
  }, []);

  const fetchJournalist = async () => {
    try {
      const response = await api.get(`/journalists/${id}`);

      if (response.data && response.data.data) {
        const journalistData = response.data.data;
        setJournalist(journalistData);

        // تحميل الصور
        if (journalistData.images) {
          try {
            const parsedImages = typeof journalistData.images === 'string' 
              ? JSON.parse(journalistData.images) 
              : journalistData.images;
            setImages(Array.isArray(parsedImages) ? parsedImages : []);
          } catch (e) {
            console.error("فشل في قراءة الصور:", e);
            setImages([]);
          }
        }

        // تحميل بيانات الأطفال
        if (journalistData.children_details) {
          try {
            const parsedChildren = typeof journalistData.children_details === 'string' 
              ? JSON.parse(journalistData.children_details) 
              : journalistData.children_details;
            setChildren(Array.isArray(parsedChildren) ? parsedChildren : []);
          } catch (e) {
            console.error("فشل في قراءة بيانات الأطفال:", e);
            setChildren([]);
          }
        }

        // تحميل التصريحات
        if (journalistData.statements) {
          try {
            const parsedStatements = typeof journalistData.statements === 'string' 
              ? JSON.parse(journalistData.statements) 
              : journalistData.statements;
            setStatements(Array.isArray(parsedStatements) ? parsedStatements : []);
          } catch (e) {
            console.error("فشل في قراءة التصريحات:", e);
            setStatements([]);
          }
        }

        // جلب بيانات الارتباطات
        await fetchLinkedData(journalistData);
      }
    } catch (error) {
      console.error('خطأ في جلب بيانات الصحفي:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedData = async (journalistData) => {
    try {
      // جلب بيانات المؤسسات المرتبطة
      if (journalistData.linked_institution) {
        try {
          // تحويل linked_institution إلى array من IDs
          const institutionIds = parseArrayField(journalistData.linked_institution);
          if (institutionIds.length > 0) {
            const institutionPromises = institutionIds.map(id => 
              api.get(`/institutions/${id}`).catch(e => {
                console.error(`فشل في جلب المؤسسة ${id}:`, e);
                return null;
              })
            );
            const institutionResponses = await Promise.all(institutionPromises);
            const institutionData = institutionResponses
              .filter(res => res !== null)
              .map(res => res.data.data);
            setLinkedInstitutionData(institutionData);
          }
        } catch (e) {
          console.error("فشل في جلب بيانات المؤسسات:", e);
        }
      }

      // جلب بيانات الناشطين المرتبطين
      if (journalistData.linked_activists) {
        try {
          const activistIds = parseArrayField(journalistData.linked_activists);
          const activistPromises = activistIds.map(id => api.get(`/activists/${id}`));
          const activistResponses = await Promise.all(activistPromises);
          setLinkedActivistsData(activistResponses.map(res => res.data.data));
        } catch (e) {
          console.error("فشل في جلب بيانات الناشطين:", e);
        }
      }

      // 🔗 جلب بيانات الصحفيين الآخرين المرتبطين
      if (journalistData.linked_journalists) {
        try {
          const peerIds = parseArrayField(journalistData.linked_journalists)
            .filter(pid => pid != journalistData.id);
          const peerPromises = peerIds.map(pid =>
            api.get(`/journalists/${pid}`).catch(e => {
              console.error(`فشل في جلب الصحفي ${pid}:`, e);
              return null;
            })
          );
          const peerResponses = await Promise.all(peerPromises);
          setLinkedJournalistPeersData(
            peerResponses.filter(res => res !== null).map(res => res.data.data)
          );
        } catch (e) {
          console.error("فشل في جلب بيانات الصحفيين المرتبطين:", e);
        }
      }
    } catch (error) {
      console.error('خطأ في جلب البيانات المرتبطة:', error);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const parseArrayField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch {
      return [];
    }
  };

  const getSocialMediaInfo = (url) => {
    if (!url) return { icon: 'fas fa-link', name: 'رابط', color: 'secondary' };
    
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('facebook.com') || urlLower.includes('fb.com')) {
      return { icon: 'fab fa-facebook', name: 'فيسبوك', color: 'primary' };
    } else if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) {
      return { icon: 'fab fa-twitter', name: 'تويتر', color: 'info' };
    } else if (urlLower.includes('instagram.com')) {
      return { icon: 'fab fa-instagram', name: 'إنستغرام', color: 'danger' };
    } else if (urlLower.includes('linkedin.com')) {
      return { icon: 'fab fa-linkedin', name: 'لينكد إن', color: 'primary' };
    } else if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
      return { icon: 'fab fa-youtube', name: 'يوتيوب', color: 'danger' };
    } else if (urlLower.includes('telegram.org') || urlLower.includes('t.me')) {
      return { icon: 'fab fa-telegram', name: 'تيليغرام', color: 'info' };
    } else if (urlLower.includes('whatsapp.com') || urlLower.includes('wa.me')) {
      return { icon: 'fab fa-whatsapp', name: 'واتساب', color: 'success' };
    } else if (urlLower.includes('tiktok.com')) {
      return { icon: 'fab fa-tiktok', name: 'تيك توك', color: 'dark' };
    } else if (urlLower.includes('snapchat.com') || urlLower.includes('snap.com')) {
      return { icon: 'fab fa-snapchat', name: 'سناب شات', color: 'warning' };
    } else {
      return { icon: 'fas fa-globe', name: 'موقع', color: 'secondary' };
    }
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    setImageZoomed(false);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setImageZoomed(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
    setImageZoomed(false);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    setImageZoomed(false);
  };

  const toggleZoom = () => {
    setImageZoomed(!imageZoomed);
  };

  const downloadImage = () => {
    const link = document.createElement('a');
    link.href = `${FILE_BASE_URL}/uploads/images/${images[currentImageIndex]}`;
    link.download = images[currentImageIndex];
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openStatementModal = (statement) => {
    setSelectedStatement(statement);
    setStatementModalOpen(true);
  };

  const closeStatementModal = () => {
    setSelectedStatement(null);
    setStatementModalOpen(false);
  };

  const stripHtmlTags = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getYouTubeVideoId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getFileIcon = (filename) => {
    const extension = filename.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return { icon: 'fas fa-file-pdf', color: '#dc3545' };
      case 'doc':
      case 'docx':
        return { icon: 'fas fa-file-word', color: '#2b579a' };
      case 'xls':
      case 'xlsx':
        return { icon: 'fas fa-file-excel', color: '#217346' };
      case 'ppt':
      case 'pptx':
        return { icon: 'fas fa-file-powerpoint', color: '#d24726' };
      case 'txt':
        return { icon: 'fas fa-file-alt', color: '#6c757d' };
      case 'zip':
      case 'rar':
        return { icon: 'fas fa-file-archive', color: '#fd7e14' };
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return { icon: 'fas fa-file-image', color: '#20c997' };
      default:
        return { icon: 'fas fa-file', color: '#6c757d' };
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'ذكر': 'primary',
      'أنثى': 'danger',
      'متزوج': 'success',
      'أعزب': 'info',
      'مطلق': 'warning',
      'أرمل': 'secondary'
    };
    return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center">
        <Spinner color="primary" size="lg" />
        <p className="mt-2">جاري تحميل بيانات الصحفي...</p>
      </div>
    );
  }

  if (!journalist) {
    return (
      <Card>
        <CardBody className="text-center">
          <h4>لم يتم العثور على الصحفي</h4>
          <Link to="/dashboard/journalists">
            <Button color="primary">العودة إلى قائمة الصحفيين</Button>
          </Link>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      {/* CSS Styles */}
      <style>{`
        .profile-header-card {
          background: linear-gradient(135deg, #34c38f 0%, #28a745 100%);
          color: white;
          border: none;
          box-shadow: 0 10px 30px rgba(52, 195, 143, 0.3);
        }
        .profile-header-card .card-body { padding: 2rem; }
        .profile-header-card h2 {
          color: white !important;
          font-weight: bold;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        .profile-header-card .profile-info-quick p {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.1rem;
        }
        .profile-header-card .profile-info-quick i { color: #ffd700 !important; }
        .info-card {
          border: none;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          border-radius: 12px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .info-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        .info-card .card-title {
          border-bottom: 2px solid #e3f2fd;
          padding-bottom: 10px;
          margin-bottom: 20px;
          font-weight: bold;
        }
        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f8f9fa;
        }
        .info-item:last-child { border-bottom: none; }
        .info-item strong {
          color: #495057;
          font-weight: 600;
          flex: 0 0 40%;
        }
        .info-item span {
          color: #6c757d;
          flex: 1;
          text-align: left;
        }
        .nav-tabs-custom .nav-link {
          border: none;
          border-radius: 25px;
          margin: 0 5px;
          transition: all 0.3s ease;
          font-weight: 500;
        }
        .nav-tabs-custom .nav-link:hover {
          background-color: #34c38f;
          color: #ffffff !important;
          transform: translateY(-2px);
        }
        .nav-tabs-custom .nav-link.active {
          background-color: #34c38f!important;
          color: #ffffff !important;
          box-shadow: 0 4px 15px rgba(52, 195, 143, 0.3);
        }
        .description-content {
          background-color: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #34c38f;
          line-height: 1.8;
        }
        .statement-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: none;
          border-left: 4px solid #28a745;
          margin-bottom: 15px;
        }
        .statement-content {
          font-style: italic;
          line-height: 1.6;
          padding: 15px;
        }
        .image-item img:hover {
          transform: scale(1.05);
          transition: transform 0.3s ease;
        }
        .social-link-item, .video-link-item, .attachment-item {
          display: inline-block;
          margin-right: 10px;
          margin-bottom: 10px;
          transition: transform 0.2s ease;
        }
        .social-link-item:hover, .video-link-item:hover, .attachment-item:hover {
          transform: translateY(-2px);
        }
        .children-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #495057;
        }
        /* Attachment Card Styles */
        .attachment-card {
          transition: all 0.3s ease;
          border-radius: 12px !important;
          cursor: pointer;
        }
        .attachment-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }
        .file-icon-wrapper {
          transition: all 0.3s ease;
        }
        .attachment-card:hover .file-icon-wrapper {
          transform: scale(1.1);
        }
        .file-icon-container {
          margin: 0 auto;
        }
        .file-name {
          font-weight: 600;
          color: #495057;
        }
        .file-actions {
          display: flex;
          justify-content: center;
          gap: 5px;
        }
        
        /* Enhanced Video Card Styles */
        .video-card {
          transition: all 0.3s ease;
          border-radius: 12px !important;
          overflow: hidden;
        }
        .video-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }
        .video-thumbnail-container {
          position: relative;
          border-radius: 8px 8px 0 0;
        }
        .video-thumbnail-img {
          transition: all 0.3s ease;
        }
        .video-card:hover .video-thumbnail-img {
          transform: scale(1.05);
        }
        .video-overlay {
          background: rgba(0, 0, 0, 0.1);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .video-card:hover .video-overlay {
          opacity: 1;
        }
        .video-badge {
          font-size: 0.75rem;
          padding: 0.4rem 0.8rem;
        }
        /* Statement Card Styles */
        .statement-preview-card {
          border: none;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          border-radius: 12px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .statement-preview-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        .statement-preview-content {
          line-height: 1.6;
          color: #6c757d;
        }
        /* Statement Modal Styles */
        .statement-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
          padding: 20px;
        }
        .statement-modal-content {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 80vh;
          overflow-y: auto;
          position: relative;
          animation: slideIn 0.3s ease;
        }
        .statement-modal-header {
          background: linear-gradient(135deg, #34c38f 0%, #28a745 100%);
          color: white;
          padding: 20px;
          border-radius: 12px 12px 0 0;
          position: relative;
        }
        .statement-modal-close {
          position: absolute;
          top: 15px;
          right: 15px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 50%;
          width: 35px;
          height: 35px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }
        .statement-modal-close:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }
        .statement-modal-body {
          padding: 30px;
          line-height: 1.8;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        /* Lightbox Styles */
        .custom-lightbox-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.95);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
          padding: 20px;
        }
        .custom-lightbox-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          max-width: 95vw;
          max-height: 95vh;
        }
        .custom-lightbox-main-area {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          width: 100%;
          margin-bottom: 20px;
        }
        .custom-lightbox-image {
          max-width: 75vw;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7);
          transition: transform 0.3s ease;
          cursor: zoom-in;
        }
        .custom-lightbox-image.zoomed {
          transform: scale(2);
          cursor: zoom-out;
        }
        .custom-lightbox-close {
          position: absolute;
          top: -25px;
          right: -25px;
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          color: #333;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 10001;
          backdrop-filter: blur(15px);
          font-size: 18px;
          font-weight: bold;
        }
        .custom-lightbox-close:hover {
          background: rgba(220, 53, 69, 0.95);
          color: white;
          transform: scale(1.1);
          box-shadow: 0 5px 20px rgba(220, 53, 69, 0.4);
        }
        .custom-lightbox-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 50%;
          width: 45px;
          height: 45px;
          color: #333;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          backdrop-filter: blur(15px);
          font-size: 16px;
          z-index: 10000;
        }
        .custom-lightbox-nav:hover {
          background: rgba(255, 255, 255, 1);
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
        }
        .custom-lightbox-prev { left: -35px; }
        .custom-lightbox-next { right: -35px; }
        .custom-lightbox-controls {
          background: rgba(0, 0, 0, 0.85);
          padding: 18px 30px;
          border-radius: 50px;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          display: flex;
          gap: 20px;
          align-items: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        }
        .custom-lightbox-btn {
          background: rgba(255, 255, 255, 0.15);
          border: none;
          border-radius: 12px;
          width: 50px;
          height: 50px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          font-size: 18px;
        }
        .custom-lightbox-btn:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(255, 255, 255, 0.2);
        }
        .custom-lightbox-counter {
          color: white;
          font-size: 18px;
          font-weight: 600;
          padding: 0 20px;
          white-space: nowrap;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 25px;
          padding: 12px 20px;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @media (max-width: 768px) {
          .nav-tabs-custom { flex-wrap: wrap; }
          .nav-tabs-custom .nav-item { margin-bottom: 5px; }
          .profile-header-card .profile-info-quick {
            text-align: center;
            margin-top: 20px;
          }
        }
      `}</style>

      {/* Header Profile Card */}
      <Card className="profile-header-card">
        <CardBody>
          <Row className="align-items-center">
            <Col lg={2} md={3} className="text-center">
              {journalist.profile_image ? (
                <img
                  src={`${FILE_BASE_URL}/uploads/images/${journalist.profile_image}`}
                  alt={journalist.full_name}
                  className="rounded-circle img-thumbnail"
                  style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                />
              ) : (
                <div 
                  className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto"
                  style={{ width: '120px', height: '120px', fontSize: '48px', color: '#6c757d' }}
                >
                  <i className="mdi mdi-account"></i>
                </div>
              )}
            </Col>
            <Col lg={10} md={9}>
              <Row className="align-items-center">
                <Col>
                  <h2 className="mb-3">{journalist.full_name || 'غير محدد'}</h2>
                  <Row className="profile-info-quick">
                    <Col md={6}>
                      <p className="mb-2"><i className="mdi mdi-account-tie me-2"></i>التخصص: {journalist.specialization || 'غير محدد'}</p>
                      <p className="mb-2">
                        <i className="mdi mdi-vote me-2"></i>الميول السياسية: 
                        {parseArrayField(journalist.political_orientation).length > 0 ? (
                          <span>
                            {parseArrayField(journalist.political_orientation).map((orientation, index) => (
                              <Badge key={index} color="light" className="ms-1 text-dark">
                                {orientation}
                              </Badge>
                            ))}
                          </span>
                        ) : (
                          ' غير محدد'
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2"><i className="mdi mdi-phone me-2"></i>الجوال: {journalist.mobile1 || 'غير محدد'}</p>
                      <p className="mb-2"><i className="mdi mdi-email me-2"></i>البريد: {journalist.email || 'غير محدد'}</p>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} md="auto" className="text-center text-md-end mt-3 mt-md-0">
                  <div className="d-flex gap-2 justify-content-center justify-content-md-end">
                    <Link to={`/dashboard/journalists/edit/${journalist.id}`}>
                      <Button color="success" size="lg">
                        <i className="mdi mdi-pencil me-1"></i>
                        تعديل
                      </Button>
                    </Link>
                    <Link to="/dashboard/journalists">
                      <Button color="light" size="lg">
                        <i className="mdi mdi-arrow-left me-1"></i>
                        عودة
                      </Button>
                    </Link>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Navigation Tabs */}
      <Card>
        <CardBody>
          <Nav tabs className="nav-tabs-custom mb-4">
            <NavItem>
              <NavLink 
                className={classnames({ active: activeTab === '1' })} 
                onClick={() => toggleTab('1')}
                style={{ cursor: 'pointer' }}
              >
                <i className="mdi mdi-account-details me-1"></i>
                المعلومات الأساسية
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink 
                className={classnames({ active: activeTab === '2' })} 
                onClick={() => toggleTab('2')}
                style={{ cursor: 'pointer' }}
              >
                <i className="mdi mdi-folder-multiple-image me-1"></i>
                المرفقات
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink 
                className={classnames({ active: activeTab === '3' })} 
                onClick={() => toggleTab('3')}
                style={{ cursor: 'pointer' }}
              >
                <i className="mdi mdi-comment-quote me-1"></i>
                التصريحات
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink 
                className={classnames({ active: activeTab === '4' })} 
                onClick={() => toggleTab('4')}
                style={{ cursor: 'pointer' }}
              >
                <i className="mdi mdi-link me-1"></i>
                الارتباطات
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* Tab 1: المعلومات الأساسية الشاملة */}
            <TabPane tabId="1">
              {/* الصف الأول: البيانات الأساسية والعنوان ومعلومات الاتصال */}
              <Row className="mb-4">
                {/* البيانات الأساسية */}
                <Col lg={4} md={6}>
                  <Card className="info-card h-100">
                    <CardBody>
                      <h5 className="card-title text-primary mb-4">
                        <i className="mdi mdi-account-details me-2"></i>
                        البيانات الأساسية
                      </h5>
                      
                      <div className="info-item">
                        <strong>الاسم الكامل:</strong>
                        <span className="text-dark">{journalist.full_name}</span>
                      </div>

                      <div className="info-item">
                        <strong>الجنس:</strong>
                        <span>{journalist.gender || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>رقم الهوية:</strong>
                        <span>{journalist.id_number || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>تاريخ الميلاد:</strong>
                        <span dir="ltr">{formatDate(journalist.birth_date)}</span>
                      </div>

                      <div className="info-item">
                        <strong>الحالة الاجتماعية:</strong>
                        <span>{journalist.marital_status || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>تاريخ الإضافة:</strong>
                        <span dir="ltr">{formatDate(journalist.created_at)}</span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* العنوان */}
                <Col lg={4} md={6}>
                  <Card className="info-card h-100">
                    <CardBody>
                      <h5 className="card-title text-success mb-4">
                        <i className="mdi mdi-map-marker me-2"></i>
                        العنوان
                      </h5>
                      
                      <div className="info-item">
                        <strong>الدولة:</strong>
                        <span>{journalist.country || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>المحافظة:</strong>
                        <span>{journalist.country_province || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>المدينة/المنطقة:</strong>
                        <span>{journalist.city_or_area || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>تفاصيل العنوان:</strong>
                        <span>{journalist.address_details || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>الإحداثيات:</strong>
                        <span>
                          {journalist.map_address ? (
                            <div className="d-flex align-items-center">
                              <span className="me-2">{journalist.map_address}</span>
                              <a
                                href={`https://www.google.com/maps?q=${journalist.map_address}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-success btn-sm"
                              >
                                <i className="mdi mdi-map me-1"></i>
                                خرائط جوجل
                              </a>
                            </div>
                          ) : (
                            'غير محدد'
                          )}
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* معلومات الاتصال */}
                <Col lg={4} md={12}>
                  <Card className="info-card h-100">
                    <CardBody>
                      <h5 className="card-title text-info mb-4">
                        <i className="mdi mdi-phone me-2"></i>
                        معلومات الاتصال
                      </h5>
                      
                      <div className="info-item">
                        <strong>جوال:</strong>
                        <span>{journalist.mobile1 || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>واتساب:</strong>
                        <span>{journalist.whatsapp1_code && journalist.whatsapp1_number ? 
                          `${journalist.whatsapp1_code}${journalist.whatsapp1_number}` : 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>البريد الإلكتروني:</strong>
                        <span>
                          {journalist.email ? (
                            <a href={`mailto:${journalist.email}`} className="text-decoration-none">
                              {journalist.email}
                            </a>
                          ) : 'غير محدد'}
                        </span>
                      </div>

                      <div className="info-item">
                        <strong>تلفون/فاكس:</strong>
                        <span>{journalist.phone_fax || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>الموقع الإلكتروني:</strong>
                        <span>
                          {journalist.website ? (
                            <a href={journalist.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                              {journalist.website}
                            </a>
                          ) : 'غير محدد'}
                        </span>
                      </div>

                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* الصف الثاني: التخصص والعمل */}
              <Row className="mb-4">
                {/* التخصص والتعليم */}
                <Col lg={6} md={6}>
                  <Card className="info-card h-100">
                    <CardBody>
                      <h5 className="card-title text-warning mb-4">
                        <i className="mdi mdi-school me-2"></i>
                        التخصص والعمل
                      </h5>
                      
                      <div className="info-item">
                        <strong>التخصص:</strong>
                        <span>{journalist.specialization || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>اسم الجامعة:</strong>
                        <span>{journalist.university_name || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>مكان العمل الأخير:</strong>
                        <span>{journalist.last_work_place || 'غير محدد'}</span>
                      </div>

                      <div className="info-item">
                        <strong>قطاع العمل:</strong>
                        <div className="mt-2">
                          {parseArrayField(journalist.work_sector).length > 0 ? (
                            <div className="d-flex flex-wrap">
                              {parseArrayField(journalist.work_sector).map((sector, index) => (
                                <Badge key={index} color="warning" className="me-2 mb-1 p-2">
                                  {sector}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span>غير محدد</span>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* حسابات التواصل الاجتماعي */}
                <Col lg={6} md={6}>
                  <Card className="info-card h-100">
                    <CardBody>
                      <h5 className="card-title text-warning mb-4">
                        <i className="mdi mdi-share-variant me-2"></i>
                        حسابات التواصل الاجتماعي
                      </h5>

                      {parseArrayField(journalist.social_accounts).length > 0 ? (
                        <div className="social-accounts-grid">
                          {parseArrayField(journalist.social_accounts).map((account, index) => {
                            const socialInfo = getSocialMediaInfo(account);
                            return (
                              <div key={index} className="social-account-item mb-3">
                                <div className="d-flex align-items-center p-2 bg-light rounded border">
                                  <div className="me-3">
                                    <i 
                                      className={`${socialInfo.icon} text-${socialInfo.color}`}
                                      style={{ fontSize: '1.3rem' }}
                                    ></i>
                                  </div>
                                  <div className="flex-grow-1">
                                    <small className="text-muted d-block mb-1">{socialInfo.name}</small>
                                    <a 
                                      href={account} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-decoration-none small text-truncate d-block"
                                      title={account}
                                    >
                                      {account.replace(/^https?:\/\//, '')}
                                    </a>
                                  </div>
                                  <div className="ms-2">
                                    <a 
                                      href={account} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      <i className="fas fa-external-link-alt"></i>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="fas fa-share-alt" style={{ fontSize: '2rem' }}></i>
                          <p className="mt-2 mb-0">لا توجد حسابات تواصل اجتماعي</p>
                        </div>
                      )}

                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* الصف الثالث: السيرة الذاتية */}
              <Row className="mb-4">
                <Col md={12}>
                  <Card className="info-card">
                    <CardBody>
                      <h5 className="card-title text-purple mb-4">
                        <i className="mdi mdi-file-document-outline me-2"></i>
                        السيرة الذاتية
                      </h5>
                      {journalist.bio ? (
                        <div className="description-content">
                          <div dangerouslySetInnerHTML={{ __html: journalist.bio }} />
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="mdi mdi-file-document-outline" style={{ fontSize: '48px' }}></i>
                          <p className="mt-2">لم يتم إضافة سيرة ذاتية</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* الصف الرابع: معلومات الأبناء */}
              <Row>
                {/* معلومات الأبناء */}
                {children.length > 0 && (
                  <Col md={12}>
                    <Card className="info-card h-100">
                      <CardBody>
                        <h5 className="card-title text-info mb-4">
                          <i className="mdi mdi-account-child me-2"></i>
                          معلومات الأبناء
                        </h5>
                        <div className="table-responsive">
                          <Table hover className="children-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>الاسم</th>
                                <th>الجنس</th>
                                <th>تاريخ الميلاد</th>
                              </tr>
                            </thead>
                            <tbody>
                              {children.map((child, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{child.name}</td>
                                  <td>{getStatusBadge(child.gender)}</td>
                                  <td dir="ltr">{formatDate(child.birth_date)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>
            </TabPane>

            {/* Tab 2: المرفقات */}
            <TabPane tabId="2">
              {/* الصور */}
              {images.length > 0 && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody>
                        <h5 className="card-title text-success mb-4">
                          <i className="mdi mdi-image me-2"></i>
                          الصور ({images.length})
                        </h5>
                        <Row>
                          {images.map((image, index) => (
                            <Col key={index} lg={3} md={4} sm={6} className="mb-3">
                              <div className="image-item">
                                <img
                                  src={`${FILE_BASE_URL}/uploads/images/${image}`}
                                  alt={`صورة ${index + 1}`}
                                  className="img-fluid rounded shadow-sm"
                                  style={{ 
                                    height: '200px', 
                                    width: '100%', 
                                    objectFit: 'cover',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => openLightbox(index)}
                                />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* فيديوهات يوتيوب */}
              {parseArrayField(journalist.videos).length > 0 ? (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody>
                        <h5 className="card-title text-primary mb-4">
                          <i className="fab fa-youtube me-2"></i>
                          مقاطع اليوتيوب ({parseArrayField(journalist.videos).length})
                        </h5>
                        <Row>
                          {parseArrayField(journalist.videos).map((video, index) => {
                            const videoId = getYouTubeVideoId(video);
                            const thumbnailUrl = videoId 
                              ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` 
                              : `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                            
                            return (
                              <Col key={index} lg={4} md={6} className="mb-4">
                                <Card className="video-card h-100 border-0 shadow-sm">
                                  <div className="video-thumbnail-container position-relative overflow-hidden">
                                    {thumbnailUrl ? (
                                      <>
                                        <img
                                          src={thumbnailUrl}
                                          alt={`فيديو يوتيوب ${index + 1}`}
                                          className="video-thumbnail-img"
                                          style={{ 
                                            width: '100%', 
                                            height: '200px', 
                                            objectFit: 'cover',
                                            borderRadius: '8px 8px 0 0'
                                          }}
                                        />
                                        <div className="video-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                                          <div 
                                            className="video-play-button bg-danger text-white rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                            style={{ 
                                              width: '60px', 
                                              height: '60px', 
                                              fontSize: '1.5rem',
                                              cursor: 'pointer',
                                              transition: 'all 0.3s ease'
                                            }}
                                            onClick={() => window.open(video, '_blank')}
                                            onMouseEnter={(e) => {
                                              e.target.style.transform = 'scale(1.1)';
                                              e.target.style.backgroundColor = '#c82333';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.target.style.transform = 'scale(1)';
                                              e.target.style.backgroundColor = '#dc3545';
                                            }}
                                          >
                                            <i className="fab fa-youtube"></i>
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <div 
                                        className="video-placeholder d-flex align-items-center justify-content-center bg-light border"
                                        style={{ height: '200px', borderRadius: '8px 8px 0 0' }}
                                      >
                                        <div className="text-center">
                                          <i className="fab fa-youtube text-danger" style={{ fontSize: '3rem' }}></i>
                                          <p className="mt-3 text-muted mb-0">فيديو يوتيوب</p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <CardBody className="p-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                      <h6 className="video-title mb-2 text-dark">
                                        <i className="fab fa-youtube text-danger me-2"></i>
                                        فيديو يوتيوب {index + 1}
                                      </h6>
                                      <Badge color="danger" pill className="video-badge">
                                        <i className="fab fa-youtube me-1"></i>
                                        YouTube
                                      </Badge>
                                    </div>
                                    <div className="video-actions mt-3">
                                      <Button 
                                        color="danger" 
                                        size="sm" 
                                        className="me-2"
                                        onClick={() => window.open(video, '_blank')}
                                      >
                                        <i className="fas fa-play me-1"></i>
                                        مشاهدة
                                      </Button>
                                      <Button 
                                        color="outline-secondary" 
                                        size="sm"
                                        onClick={() => {
                                          navigator.clipboard.writeText(video);
                                          // يمكنك إضافة إشعار هنا
                                        }}
                                      >
                                        <i className="fas fa-copy me-1"></i>
                                        نسخ الرابط
                                      </Button>
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody className="text-center py-5">
                        <i className="fab fa-youtube text-muted" style={{ fontSize: "48px" }}></i>
                        <h5 className="text-muted mt-3">لا توجد مقاطع فيديو</h5>
                        <p className="text-muted">لم يتم إضافة أي مقاطع فيديو يوتيوب لهذا الصحفي</p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* ملفات الفيديو المرفوعة */}
              {parseArrayField(journalist.video_files).length > 0 ? (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody>
                        <h5 className="card-title mb-4" style={{ color: '#6f42c1' }}>
                          <i className="fas fa-video me-2"></i>
                          ملفات الفيديو ({parseArrayField(journalist.video_files).length})
                        </h5>
                        <Row>
                          {parseArrayField(journalist.video_files).map((videoFile, index) => {
                            const fileName = videoFile.split('/').pop() || videoFile;
                            const truncatedName = fileName.length > 30 ? fileName.substring(0, 30) + '...' : fileName;

                            return (
                              <Col key={index} lg={6} md={12} className="mb-4">
                                <Card className="video-card h-100 border-0 shadow-sm">
                                  <CardBody className="p-3">
                                    <div className="video-container mb-3" style={{ backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                                      <video controls className="w-100" style={{ maxHeight: '300px' }} preload="metadata">
                                        <source
                                          src={`${FILE_BASE_URL}/uploads/videos/${videoFile}`}
                                          type={`video/${videoFile.split('.').pop()?.toLowerCase() || 'mp4'}`}
                                        />
                                        متصفحك لا يدعم تشغيل الفيديو
                                      </video>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center">
                                        <i className="fas fa-video me-2" style={{ color: '#6f42c1' }}></i>
                                        <span className="text-dark fw-medium" title={fileName}>{truncatedName}</span>
                                      </div>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => {
                                          const link = document.createElement('a');
                                          link.href = `${FILE_BASE_URL}/uploads/videos/${videoFile}`;
                                          link.download = fileName;
                                          link.click();
                                        }}
                                      >
                                        <i className="fas fa-download me-1"></i>
                                        تحميل
                                      </Button>
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody className="text-center py-5">
                        <i className="fas fa-video text-muted" style={{ fontSize: "48px" }}></i>
                        <h5 className="text-muted mt-3">لا توجد ملفات فيديو</h5>
                        <p className="text-muted">لم يتم رفع أي ملفات فيديو لهذا الصحفي</p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* مرفقات أخرى */}
              {parseArrayField(journalist.attachments).length > 0 ? (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody>
                        <h5 className="card-title text-primary mb-4">
                          <i className="fas fa-paperclip me-2"></i>
                          المرفقات الأخرى ({parseArrayField(journalist.attachments).length})
                        </h5>
                        <Row>
                          {parseArrayField(journalist.attachments).map((attachment, index) => {
                            const fileInfo = getFileIcon(attachment);
                            const fileName = attachment.split('/').pop() || attachment;
                            const fileExtension = fileName.split('.').pop()?.toUpperCase() || 'FILE';
                            const truncatedName = fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName;
                            // حساب حجم افتراضي للملف (يمكنك استبداله بحجم حقيقي من API)
                            const fileSize = Math.floor(Math.random() * 5000) + 100;
                            const fileSizeFormatted = fileSize > 1024 ? `${(fileSize / 1024).toFixed(1)} MB` : `${fileSize} KB`;
                            
                            return (
                              <Col key={index} lg={3} md={4} sm={6} className="mb-4">
                                <Card className="attachment-card h-100 border-0 shadow-sm">
                                  <CardBody className="text-center p-4">
                                    <div className="file-icon-container mb-3">
                                      <div 
                                        className="file-icon-wrapper d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm"
                                        style={{ 
                                          width: '80px', 
                                          height: '80px', 
                                          backgroundColor: `${fileInfo.color}15`,
                                          border: `2px solid ${fileInfo.color}30`
                                        }}
                                      >
                                        <i 
                                          className={fileInfo.icon}
                                          style={{ 
                                            fontSize: '2.5rem', 
                                            color: fileInfo.color 
                                          }}
                                        ></i>
                                      </div>
                                    </div>
                                    
                                    <h6 
                                      className="file-name mb-2 text-dark fw-bold"
                                      style={{ 
                                        fontSize: '0.9rem', 
                                        lineHeight: '1.3',
                                        minHeight: '2.6rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      title={fileName}
                                    >
                                      {truncatedName}
                                    </h6>
                                    
                                    <div className="file-info mb-3">
                                      <Badge 
                                        style={{ 
                                          backgroundColor: fileInfo.color,
                                          fontSize: '0.75rem',
                                          padding: '0.4rem 0.8rem'
                                        }}
                                        className="text-white mb-2"
                                      >
                                        {fileExtension}
                                      </Badge>
                                      <div className="file-size text-muted small">
                                        <i className="fas fa-weight-hanging me-1"></i>
                                        {fileSizeFormatted}
                                      </div>
                                    </div>
                                    
                                    <div className="file-actions d-flex gap-2 justify-content-center">
                                      <Button 
                                        color="primary" 
                                        size="sm"
                                        className="flex-fill"
                                        onClick={() => window.open(`${FILE_BASE_URL}/uploads/attachments/${attachment}`, '_blank')}
                                        style={{ fontSize: '0.8rem' }}
                                      >
                                        <i className="fas fa-eye me-1"></i>
                                        عرض
                                      </Button>
                                      <Button 
                                        color="success" 
                                        size="sm"
                                        className="flex-fill"
                                        onClick={() => {
                                          const link = document.createElement('a');
                                          link.href = `${FILE_BASE_URL}/uploads/attachments/${attachment}`;
                                          link.download = fileName;
                                          link.click();
                                        }}
                                        style={{ fontSize: '0.8rem' }}
                                      >
                                        <i className="fas fa-download me-1"></i>
                                        تحميل
                                      </Button>
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody className="text-center py-5">
                        <i className="fas fa-paperclip text-muted" style={{ fontSize: "48px" }}></i>
                        <h5 className="text-muted mt-3">لا توجد مرفقات أخرى</h5>
                        <p className="text-muted">لم يتم إضافة أي ملفات أو مستندات لهذا الصحفي</p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* رسالة في حال عدم وجود مرفقات */}
              {images.length === 0 && 
               parseArrayField(journalist.videos).length === 0 && 
               parseArrayField(journalist.video_files).length === 0 && 
               parseArrayField(journalist.attachments).length === 0 && (
                <Row>
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody className="text-center py-5">
                        <i className="mdi mdi-folder-open text-muted" style={{ fontSize: '64px' }}></i>
                        <h5 className="text-muted mt-3">لا توجد مرفقات</h5>
                        <p className="text-muted">لم يتم إضافة أي صور أو فيديوهات أو ملفات لهذا الصحفي</p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </TabPane>

            {/* Tab 3: التصريحات */}
            <TabPane tabId="3">
              {statements.length > 0 ? (
                <Row>
                  <Col md={12}>
                    <div className="mb-4">
                      <h5 className="text-primary mb-4">
                        <i className="mdi mdi-comment-quote me-2"></i>
                        التصريحات والأقوال ({statements.length})
                      </h5>
                      <Row>
                        {statements.map((statement, index) => (
                          <Col key={index} lg={6} md={6} className="mb-4">
                            <Card 
                              className="statement-preview-card h-100"
                              onClick={() => openStatementModal(statement)}
                            >
                              <CardBody className="p-4">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <h6 className="text-primary mb-0 flex-grow-1 fw-bold">
                                    {statement.title || `تصريح ${index + 1}`}
                                  </h6>
                                  <Badge color="light" className="ms-2 text-primary">
                                    #{index + 1}
                                  </Badge>
                                </div>
                                
                                <div className="statement-preview-content mb-3">
                                  {stripHtmlTags(statement.content).length > 150 
                                    ? stripHtmlTags(statement.content).substring(0, 150) + '...'
                                    : stripHtmlTags(statement.content)
                                  }
                                </div>
                                
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">
                                    <i className="mdi mdi-calendar me-1"></i>
                                    {formatDate(statement.created_at)}
                                  </small>
                                  <Button 
                                    color="primary" 
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openStatementModal(statement);
                                    }}
                                  >
                                    <i className="mdi mdi-eye me-1"></i>
                                    المزيد
                                  </Button>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col md={12}>
                    <Card className="info-card">
                      <CardBody className="text-center py-5">
                        <i className="mdi mdi-comment-quote text-muted" style={{ fontSize: '64px' }}></i>
                        <h5 className="text-muted mt-3">لا توجد تصريحات</h5>
                        <p className="text-muted">لم يتم إضافة أي تصريحات أو أقوال لهذا الصحفي</p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </TabPane>

            {/* Tab 4: الارتباطات */}
            <TabPane tabId="4">
              <Row className="mb-4">
                {/* المؤسسات المرتبطة */}
                <Col md={6} className="mb-4">
                  <Card className="info-card">
                    <CardBody>
                      <h5 className="card-title text-primary mb-3">
                        <i className="mdi mdi-office-building me-2"></i>
                        المؤسسات المرتبطة ({linkedInstitutionData?.length || 0})
                      </h5>
                      {linkedInstitutionData && linkedInstitutionData.length > 0 ? (
                        <Row>
                          {linkedInstitutionData.map((institution) => (
                            <Col key={institution.id} md={12} className="mb-3">
                              <Card className="shadow-sm border-0 h-100">
                                <CardBody className="p-3">
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      {institution.logo ? (
                                        <>
                                          <img
                                            src={`${FILE_BASE_URL}/uploads/images/${institution.logo}`}
                                            alt={institution.title}
                                            className="rounded"
                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.parentNode.querySelector('.fallback-avatar').style.display = 'block';
                                            }}
                                          />
                                          <div className="fallback-avatar" style={{ display: 'none' }}>
                                            {getInstitutionAvatarCircle(institution.title, '50px', '16px')}
                                          </div>
                                        </>
                                      ) : (
                                        getInstitutionAvatarCircle(institution.title, '50px', '16px')
                                      )}
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{institution.title}</h6>
                                      <small className="text-muted">{institution.type || 'مؤسسة'}</small>
                                    </div>
                                    <div>
                                      <Link to={`/dashboard/institutions/view/${institution.id}`}>
                                        <Button color="outline-primary" size="sm">
                                          <i className="mdi mdi-eye me-1"></i>
                                          عرض
                                        </Button>
                                      </Link>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <i className="mdi mdi-office-building-outline mdi-48px mb-2"></i>
                          <p>لا يوجد مؤسسات مرتبطة بهذا الصحفي</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* الناشطين المرتبطين */}
                <Col md={6} className="mb-4">
                  <Card className="info-card">
                    <CardBody>
                      <h5 className="card-title text-success mb-3">
                        <i className="mdi mdi-account-group me-2"></i>
                        الناشطين المرتبطين ({linkedActivistsData?.length || 0})
                      </h5>
                      {linkedActivistsData && linkedActivistsData.length > 0 ? (
                        <Row>
                          {linkedActivistsData.map((activist) => (
                            <Col key={activist.id} md={12} className="mb-3">
                              <Card className="shadow-sm border-0 h-100">
                                <CardBody className="p-3">
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      {activist.profile_image ? (
                                        <>
                                          <img
                                            src={`${FILE_BASE_URL}/uploads/images/${activist.profile_image}`}
                                            alt={activist.full_name}
                                            className="rounded-circle"
                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.parentNode.querySelector('.fallback-avatar').style.display = 'block';
                                            }}
                                          />
                                          <div className="fallback-avatar" style={{ display: 'none' }}>
                                            {getAvatarCircle(activist.full_name, '50px', '16px')}
                                          </div>
                                        </>
                                      ) : (
                                        getAvatarCircle(activist.full_name, '50px', '16px')
                                      )}
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{activist.full_name}</h6>
                                      <small className="text-muted">{activist.profession || 'ناشط'}</small>
                                    </div>
                                    <div>
                                      <Link to={`/dashboard/activists/view/${activist.id}`}>
                                        <Button color="outline-success" size="sm">
                                          <i className="mdi mdi-eye me-1"></i>
                                          عرض
                                        </Button>
                                      </Link>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <i className="mdi mdi-account-off mdi-48px mb-2"></i>
                          <p>لا يوجد ناشطين مرتبطين بهذا الصحفي</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* 🔗 الصحفيون الآخرون المرتبطون */}
                <Col md={6} className="mb-4">
                  <Card className="info-card">
                    <CardBody>
                      <h5 className="card-title text-info mb-3">
                        <i className="mdi mdi-account-multiple me-2"></i>
                        الصحفيون المرتبطون ({linkedJournalistPeersData?.length || 0})
                      </h5>
                      {linkedJournalistPeersData && linkedJournalistPeersData.length > 0 ? (
                        <Row>
                          {linkedJournalistPeersData.map((peer) => (
                            <Col key={peer.id} md={12} className="mb-3">
                              <Card className="shadow-sm border-0 h-100">
                                <CardBody className="p-3">
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      {peer.profile_image ? (
                                        <>
                                          <img
                                            src={`${FILE_BASE_URL}/uploads/images/${peer.profile_image}`}
                                            alt={peer.full_name}
                                            className="rounded-circle"
                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.parentNode.querySelector('.fallback-avatar').style.display = 'block';
                                            }}
                                          />
                                          <div className="fallback-avatar" style={{ display: 'none' }}>
                                            {getAvatarCircle(peer.full_name, '50px', '16px')}
                                          </div>
                                        </>
                                      ) : (
                                        getAvatarCircle(peer.full_name, '50px', '16px')
                                      )}
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">{peer.full_name}</h6>
                                      <small className="text-muted">{peer.specialization || 'صحفي'}</small>
                                    </div>
                                    <div>
                                      <Link to={`/dashboard/journalists/view/${peer.id}`}>
                                        <Button color="outline-info" size="sm">
                                          <i className="mdi mdi-eye me-1"></i>
                                          عرض
                                        </Button>
                                      </Link>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <i className="mdi mdi-account-off-outline mdi-48px mb-2"></i>
                          <p>لا يوجد صحفيون آخرون مرتبطون بهذا الصحفي</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>

      {/* Enhanced Professional Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="custom-lightbox-overlay" onClick={closeLightbox}>
          <div className="custom-lightbox-container" onClick={(e) => e.stopPropagation()}>
            
            <div className="custom-lightbox-main-area">
              {/* Close Button - Outside Image */}
              <button className="custom-lightbox-close" onClick={closeLightbox}>
                <i className="fas fa-times"></i>
              </button>
              
              {/* Large Image */}
              <img
                src={`${FILE_BASE_URL}/uploads/images/${images[currentImageIndex]}`}
                alt={`صورة ${currentImageIndex + 1}`}
                className={`custom-lightbox-image ${imageZoomed ? 'zoomed' : ''}`}
                onClick={toggleZoom}
              />
              
              {/* Navigation Arrows - Outside Image */}
              {images.length > 1 && (
                <>
                  <button className="custom-lightbox-nav custom-lightbox-prev" onClick={prevImage}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button className="custom-lightbox-nav custom-lightbox-next" onClick={nextImage}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </>
              )}
            </div>
            
            {/* Bottom Control Bar */}
            <div className="custom-lightbox-controls">
              <button className="custom-lightbox-btn" onClick={toggleZoom} title={imageZoomed ? "تصغير" : "تكبير"}>
                <i className={`fas ${imageZoomed ? 'fa-search-minus' : 'fa-search-plus'}`}></i>
              </button>
              <button className="custom-lightbox-btn" onClick={downloadImage} title="تحميل الصورة">
                <i className="fas fa-download"></i>
              </button>
              <span className="custom-lightbox-counter">
                {currentImageIndex + 1} من {images.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Statement Modal */}
      {statementModalOpen && selectedStatement && (
        <div className="statement-modal-overlay" onClick={closeStatementModal}>
          <div className="statement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="statement-modal-header">
              <h4 className="mb-0">{selectedStatement.title || 'تصريح'}</h4>
              <button className="statement-modal-close" onClick={closeStatementModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="statement-modal-body">
              <div dangerouslySetInnerHTML={{ __html: selectedStatement.content }} />
              {selectedStatement.created_at && (
                <div className="mt-4 pt-3 border-top">
                  <small className="text-muted">
                    <i className="mdi mdi-calendar me-1"></i>
                    تاريخ الإضافة: {formatDate(selectedStatement.created_at)}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(ViewJournalist);